import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Grid,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import { 
  DarkMode as DarkModeIcon, 
  LightMode as LightModeIcon
} from '@mui/icons-material';
import DashboardContext from '../../../../contexts/DashboardContext';
import SettingsDialogBase from './SettingsDialogBase';

const AppearanceDialog = ({ open, onClose, currentSettings, onSave }) => {
  const theme = useTheme();
  const { isDarkMode } = useContext(DashboardContext);
  
  // Languages with their display names and flags
  const languages = [
    { code: 'en', name: 'English', icon: '🇺🇸' },
    { code: 'ms', name: 'Malay', icon: '🇲🇾' },
  { code: 'id', name: 'Indonesia', icon: '🇮🇩' },
    { code: 'zh', name: 'Chinese', icon: '🇨🇳' },
    { code: 'hn', name: 'Hokkien', icon: '🇨🇳' },
    { code: 'ct', name: 'Cantonese', icon: '🇨🇳' },
    { code: 'hk', name: 'Hakka', icon: '🇨🇳' },
    { code: 'tc', name: 'Teochew', icon: '🇨🇳' },
    { code: 'ta', name: 'Tamil', icon: '🇮🇳' },
    { code: 'hi', name: 'Hindi', icon: '🇮🇳' },
    { code: 'ja', name: 'Japanese', icon: '🇯🇵' },
    { code: 'ko', name: 'Korean', icon: '🇰🇷' },
    { code: 'th', name: 'Thai', icon: '🇹🇭' },
    { code: 'vi', name: 'Vietnamese', icon: '🇻🇳' }
  ];
  
  // Local state for dialog (including theme preference)
  const [settings, setSettings] = useState({
    language: currentSettings?.language || 'English',
    theme: isDarkMode ? 'dark' : 'light'
  });

  useEffect(() => {
    if (currentSettings) {
      setSettings({
        language: currentSettings.language || 'English',
        theme: isDarkMode ? 'dark' : 'light'
      });
    }
  }, [currentSettings, isDarkMode]);
  
  const handleLanguageChange = (event) => {
    setSettings({ ...settings, language: event.target.value });
  };
  
  const handleThemeChange = (newTheme) => {
    setSettings({ ...settings, theme: newTheme });
  };
  
  const handleSave = () => {
    onSave(settings);
  };
  
  return (
    <SettingsDialogBase
      open={open}
      onClose={onClose}
      title="Display & Language Settings"
      description="Customize the appearance and language of your dashboard"
      onSave={handleSave}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Display Mode
        </Typography>
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                border: settings.theme === 'light' ? `2px solid ${theme.palette.primary.main}` : 'none',
                borderRadius: 2,
                height: '100%'
              }}
              onClick={() => handleThemeChange('light')}
            >
              <CardContent sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                bgcolor: 'background.paper',
                color: 'text.primary'
              }}>
                <LightModeIcon sx={{ fontSize: 40, mb: 1, color: 'warning.main' }} />
                <Typography variant="subtitle1" fontWeight={500}>
                  Light Mode
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                border: settings.theme === 'dark' ? `2px solid ${theme.palette.primary.main}` : 'none',
                borderRadius: 2,
                height: '100%'
              }}
              onClick={() => handleThemeChange('dark')}
            >
              <CardContent sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                bgcolor: '#121212',
                color: '#ffffff'
              }}>
                <DarkModeIcon sx={{ fontSize: 40, mb: 1, color: '#5c6bc0' }} />
                <Typography variant="subtitle1" fontWeight={500}>
                  Dark Mode
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      <Divider sx={{ my: 3 }} />
      
      <Box>
        <Typography variant="h6" gutterBottom>
          Language
        </Typography>
        
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <RadioGroup
            value={settings.language}
            onChange={handleLanguageChange}
            name="language-options"
          >
            <Grid container spacing={2}>
              {languages.map((lang) => (
                <Grid item xs={6} key={lang.code}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      border: settings.language === lang.name ? `2px solid ${theme.palette.primary.main}` : 'none',
                      borderRadius: 2,
                      height: '100%'
                    }}
                    onClick={() => setSettings({ ...settings, language: lang.name })}
                  >
                    <CardContent sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 2
                    }}>
                      <Typography variant="h5">{lang.icon}</Typography>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={500}>
                          {lang.name}
                        </Typography>
                        <FormControlLabel 
                          value={lang.name} 
                          control={<Radio />} 
                          label=""
                          sx={{ m: 0 }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
        </FormControl>
      </Box>
    </SettingsDialogBase>
  );
};

export default AppearanceDialog;